import { SchoolContentItem, SchoolCourse, SDK, StudentLibrary } from "@axl-dev/school-sdk";
import { SearchOutlined } from "@ant-design/icons";
import { Input, Select, Spin, theme } from "antd";
import { useEffect, useMemo, useState } from "react";

import "@app/pages/school-materials/ui/SchoolMaterials.scss";
import { generateBtnHoverCss, parseBtnHoverProps, sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";
import { deviceVarCss } from "@app/widgets/shared/deviceCss";
import { attr, bool } from "@app/widgets/shared/parseAttr";

import { CourseCard } from "@app/pages/school-materials/ui/SchoolCourses/components/CourseCard/CourseCard";
import { FolderCard } from "@app/pages/school-materials/ui/SchoolCourses/components/FolderCard/FolderCard";
import { LibraryCard } from "@app/pages/school-materials/ui/SchoolLibraries/components/LibraryCard/LibraryCard";
import SchoolContentBreadcrumb from "@app/pages/school-materials/ui/SchoolCourses/components/Breadcrumb/Breadcrumb";
import { SchoolContentItemType } from "@app/pages/school-materials/model/courses-provider";
import { useContentFolder } from "@app/pages/school-materials/model/use-content-folder";
import { SchoolContentSectionType } from "@app/pages/school-materials/model/school-content-item";
import { useDevice } from "@app/shared/lib/DeviceProvider";
import { useLoc } from "@app/shared/lib/hooks";

type SourceMode =
    | "allCourses"
    | "allLibraries"
    | "all"
    | "specificCourses"
    | "specificLibraries"
    | "specificMixed";

type DisplayMode = "flat" | "folders";

export type CourseListWidgetProps = {
    sdk: SDK;
    folderParam: string;
    sourceMode: SourceMode;
    displayMode: DisplayMode;
    layout: "grid" | "list";
    showProgress: boolean;
    showDescription: boolean;
    showDescriptionLabel: boolean;
    showImage: boolean;
    cardVariant: "vertical" | "horizontal" | "horizontalReversed" | "compact";
    cardGap: string;
    cardWidth: string;
    cardBorderRadius: string;
    cardBgColor: string;
    cardBorderColor: string;
    itemTitlePosition: "onImage" | "belowImage";
    itemTitleColor: string;
    itemTitleMargin: string;
    itemTitlePadding: string;
    itemTitlePaddingTablet: string;
    itemTitlePaddingMobile: string;
    imageWidth: string;
    imageHeight: string;
    btnBgColor: string;
    btnTextColor: string;
    btnBorderRadius: string;
    btnFontSize: string;
    btnPaddingV: string;
    btnPaddingH: string;
    btnWidth: string;
    progressBarColor: string;
    progressBgColor: string;
    progressHeight: string;
    progressBorderRadius: string;
    progressTextColor: string;
    progressTextSize: string;
    progressNameLines: string;
    btnContinueText: string;
    btnStartText: string;
    btnOpenText: string;
    btnStartBgColor: string;
    btnStartTextColor: string;
    showSearch: boolean;
    showFilter: boolean;
    searchPlaceholder: string;
    searchBgColor: string;
    searchTextColor: string;
    searchBorderColor: string;
    searchBorderRadius: string;
    searchFontSize: string;
    filterBgColor: string;
    filterActiveColor: string;
    filterTextColor: string;
    filterFontSize: string;
    searchBarAlign: "left" | "center" | "right";
    searchBarWidth: string;
    folderBorderRadius: string;
    folderBorderColor: string;
    folderBgColor: string;
    folderTextColor: string;
    folderFontSize: string;
    folderMarginTop: string;
    folderMarginRight: string;
    folderMarginBottom: string;
    folderMarginLeft: string;
    folderPaddingTop: string;
    folderPaddingRight: string;
    folderPaddingBottom: string;
    folderPaddingLeft: string;
    btnHoverEffect: string;
    btnHoverBgColor: string;
    btnHoverTextColor: string;
    btnHoverShadow: string;
    btnHoverScale: string;
    btnHoverTransitionDuration: string;
    courseIds?: string[];
    libraryIds?: string[];
    prefetchedCourses?: SchoolCourse[];
    prefetchedLibraries?: StudentLibrary[];
    prefetchedContentItems?: SchoolContentItem[];
};

const showsCourses = (mode: SourceMode) =>
    mode === "allCourses" || mode === "all" || mode === "specificCourses" || mode === "specificMixed";

const showsLibraries = (mode: SourceMode) =>
    mode === "allLibraries" || mode === "all" || mode === "specificLibraries" || mode === "specificMixed";

const extractCourses = (items: SchoolContentItem[]): SchoolCourse[] => {
    const courses: SchoolCourse[] = [];
    for (const item of items) {
        if (item.course) {
            courses.push(item.course);
        }
        if (item.children && item.children.length > 0) {
            courses.push(...extractCourses(item.children));
        }
    }
    return courses;
};

const extractLibraries = (items: SchoolContentItem[]): StudentLibrary[] => {
    const libraries: StudentLibrary[] = [];
    for (const item of items) {
        if (item.library) {
            libraries.push(item.library);
        }
        if (item.children && item.children.length > 0) {
            libraries.push(...extractLibraries(item.children));
        }
    }
    return libraries;
};

type SourceFilterOpts = {
    needCourses: boolean;
    needLibraries: boolean;
    needSpecificCourses: boolean;
    needSpecificLibraries: boolean;
    courseIds?: string[];
    libraryIds?: string[];
};

const leafMatchesSource = (item: SchoolContentItem, opts: SourceFilterOpts): boolean => {
    if (item.course) {
        return opts.needCourses && (!opts.needSpecificCourses || !!opts.courseIds?.includes(item.course.id));
    }
    if (item.library) {
        return opts.needLibraries && (!opts.needSpecificLibraries || !!opts.libraryIds?.includes(item.library.id));
    }
    return false;
};

const hasVisibleLeaf = (item: SchoolContentItem, opts: SourceFilterOpts): boolean => {
    if (item.type === SchoolContentItemType.Item) return leafMatchesSource(item, opts);
    return (item.children || []).some((child: SchoolContentItem) => hasVisibleLeaf(child, opts));
};

const pruneBySource = (items: SchoolContentItem[], opts: SourceFilterOpts): SchoolContentItem[] =>
    items.filter(x =>
        x.type === SchoolContentItemType.Item ? leafMatchesSource(x, opts) : hasVisibleLeaf(x, opts),
    );

const findFolderSection = (item: SchoolContentItem, opts: SourceFilterOpts): SchoolContentSectionType | undefined => {
    for (const child of (item.children || []) as SchoolContentItem[]) {
        if (child.type === SchoolContentItemType.Item) {
            if (leafMatchesSource(child, opts)) return child.library ? SchoolContentSectionType.Library : SchoolContentSectionType.Course;
        } else {
            const nested = findFolderSection(child, opts);
            if (nested) return nested;
        }
    }
    return undefined;
};

export const CourseListWidget: React.FC<CourseListWidgetProps> = (props) => {
    const sdk = props.sdk;
    const loc = useLoc();
    const device = useDevice();
    const isMobile = device.deviceSize === "mobile";
    const { token } = theme.useToken();
    const cardBg = props.cardBgColor || token.colorBgElevated;
    const cardBorder = props.cardBorderColor || token.colorBorder;
    const btnBg = props.btnBgColor || token.colorPrimary;
    const btnText = props.btnTextColor || token.colorTextLightSolid;
    const btnStartBg = props.btnStartBgColor || token.colorSuccess;
    const btnStartText = props.btnStartTextColor || token.colorTextLightSolid;
    const progressBar = props.progressBarColor || token.colorPrimary;
    const progressBg = props.progressBgColor || token.colorFillSecondary;
    const progressText = props.progressTextColor || token.colorTextSecondary;
    const titleColor = props.itemTitleColor || token.colorText;
    const [courses, setCourses] = useState<SchoolCourse[]>([]);
    const [libraries, setLibraries] = useState<StudentLibrary[]>([]);
    const [contentItems, setContentItems] = useState<SchoolContentItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState("");
    const [courseFilter, setCourseFilter] = useState<"all" | "started" | "available">("all");
    const { folder, openFolder } = useContentFolder(contentItems, props.folderParam);

    const isFolderMode = props.displayMode === "folders";
    const needCourses = useMemo(() => showsCourses(props.sourceMode), [props.sourceMode]);
    const needLibraries = useMemo(() => showsLibraries(props.sourceMode), [props.sourceMode]);
    const scopeId = useMemo(() => `ez-cl-${Math.random().toString(36).slice(2, 8)}`, []);

    const needSpecificCourses = props.sourceMode === "specificCourses" || props.sourceMode === "specificMixed";
    const needSpecificLibraries = props.sourceMode === "specificLibraries" || props.sourceMode === "specificMixed";

    const sourceFilterOpts = useMemo<SourceFilterOpts>(() => ({
        needCourses,
        needLibraries,
        needSpecificCourses,
        needSpecificLibraries,
        courseIds: props.courseIds,
        libraryIds: props.libraryIds,
    }), [needCourses, needLibraries, needSpecificCourses, needSpecificLibraries, props.courseIds, props.libraryIds]);

    const folderItems = useMemo(() => {
        if (!isFolderMode) return [];
        let items = pruneBySource(!folder ? contentItems : (folder.children || []), sourceFilterOpts);
        if (searchText) {
            const q = searchText.toLowerCase();
            items = items.filter(x =>
                (x.course?.title?.toLowerCase().includes(q)) ||
                (x.library?.name?.toLowerCase().includes(q)) ||
                (x.title?.toLowerCase().includes(q))
            );
        }
        if (courseFilter === "started") {
            items = items.filter(x =>
                !x.course || (x.course.studentCourse && (x.course.studentCourse.progress ?? 0) > 0)
            );
        } else if (courseFilter === "available") {
            items = items.filter(x =>
                !x.course || !x.course.studentCourse || (x.course.studentCourse.progress ?? 0) === 0
            );
        }
        return items;
    }, [isFolderMode, folder, contentItems, searchText, courseFilter, sourceFilterOpts]);

    const sectionType = useMemo(() => {
        const m = props.sourceMode;
        if (m === "allLibraries" || m === "specificLibraries") return SchoolContentSectionType.Library;
        return SchoolContentSectionType.Course;
    }, [props.sourceMode]);

    useEffect(() => {
        if (isFolderMode && props.prefetchedContentItems) {
            setContentItems(props.prefetchedContentItems);
            setCourses(extractCourses(props.prefetchedContentItems));
            setLibraries(extractLibraries(props.prefetchedContentItems));
            setLoading(false);
            return;
        }

        if (!isFolderMode && (props.prefetchedCourses || props.prefetchedLibraries)) {
            let filteredCourses = props.prefetchedCourses || [];
            let filteredLibraries = props.prefetchedLibraries || [];

            if (needSpecificCourses && props.courseIds && props.courseIds.length > 0) {
                filteredCourses = filteredCourses.filter(c => props.courseIds!.includes(c.id));
            }
            if (needSpecificLibraries && props.libraryIds && props.libraryIds.length > 0) {
                filteredLibraries = filteredLibraries.filter(l => props.libraryIds!.includes(l.id));
            }

            setCourses(needCourses ? filteredCourses : []);
            setLibraries(needLibraries ? filteredLibraries : []);
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);

            try {
                const promises: Promise<void>[] = [];

                if (needCourses) {
                    promises.push(
                        (async () => {
                            try {
                                const courseFilter: Record<string, any> = {
                                    courseType: "all",
                                    sectionType: SchoolContentSectionType.Course,
                                    onlyAvailable: true,
                                };
                                sdk.content.courses.store.updateFilter(courseFilter);
                                await sdk.content.courses.store.fetch();
                                if (cancelled) return;

                                const items: SchoolContentItem[] = sdk.content.courses.store.items;

                                if (isFolderMode) {
                                    if (!cancelled) setContentItems(items || []);
                                }

                                let allCourses = extractCourses(items || []);

                                if (needSpecificCourses && props.courseIds && props.courseIds.length > 0) {
                                    allCourses = allCourses.filter(c => props.courseIds!.includes(c.id));
                                }

                                if (!cancelled) setCourses(allCourses);
                            } catch (e) {
                                if (!cancelled) setError(loc.word("CourseList.failedToLoadCourses", { default: "Failed to load courses" }));
                            }
                        })(),
                    );
                }

                if (needLibraries) {
                    promises.push(
                        (async () => {
                            try {
                                const libFilter: Record<string, any> = {
                                    sectionType: SchoolContentSectionType.Library,
                                    onlyAvailable: true,
                                    useBaseFilter: false,
                                };
                                sdk.content.libraries.store.updateFilter(libFilter);
                                await sdk.content.libraries.store.fetch();
                                if (cancelled) return;

                                const items: SchoolContentItem[] = sdk.content.libraries.store.items;
                                let allLibraries = extractLibraries(items || []);

                                if (needSpecificLibraries && props.libraryIds && props.libraryIds.length > 0) {
                                    allLibraries = allLibraries.filter(l => props.libraryIds!.includes(l.id));
                                }

                                if (!cancelled) setLibraries(allLibraries);
                            } catch (e) {
                                if (!cancelled) setError(loc.word("CourseList.failedToLoadLibraries", { default: "Failed to load libraries" }));
                            }
                        })(),
                    );
                }

                await Promise.all(promises);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [sdk, needCourses, needLibraries, isFolderMode, props.courseIds, props.libraryIds, props.prefetchedCourses, props.prefetchedLibraries, props.prefetchedContentItems]);

    const titlePosition = (props.itemTitlePosition === "belowImage" || !props.showImage)
        ? "belowImage" as const
        : "onImage" as const;

    const courseButtonLabels = (props.btnContinueText || props.btnStartText)
        ? { continue: props.btnContinueText || undefined, start: props.btnStartText || undefined }
        : undefined;

    const filteredCourses = useMemo(() => {
        let result = courses;
        if (searchText) {
            const q = searchText.toLowerCase();
            result = result.filter(c => c.title?.toLowerCase().includes(q));
        }
        if (courseFilter === "started") {
            result = result.filter(c => c.studentCourse && (c.studentCourse.progress ?? 0) > 0);
        } else if (courseFilter === "available") {
            result = result.filter(c => !c.studentCourse || (c.studentCourse.progress ?? 0) === 0);
        }
        return result;
    }, [courses, searchText, courseFilter]);

    const filteredLibraries = useMemo(() => {
        if (!searchText) return libraries;
        const q = searchText.toLowerCase();
        return libraries.filter(l => l.name?.toLowerCase().includes(q));
    }, [libraries, searchText]);

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                <Spin />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ color: "red", padding: "20px" }}>
                {error}
            </div>
        );
    }

    const isGrid = props.layout === "grid";

    const containerStyle: React.CSSProperties = isGrid
        ? { width: "100%" }
        : { display: "flex", flexDirection: "column", gap: props.cardGap || "20px", width: "100%" };

    const variant = props.cardVariant || "vertical";
    const isHoriz = variant === "horizontal" || variant === "horizontalReversed";
    const nameLines = parseInt(props.progressNameLines || "2", 10) || 2;
    const showImg = props.showImage;
    const isBelowImage = props.itemTitlePosition === "belowImage" || !showImg;
    const btnPad = `${sc(props.btnPaddingV || "4px")} ${sc(props.btnPaddingH || "15px")}`;

    const scopedCss = `
        .${scopeId} {
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
        }
        ${deviceVarCss(`.${scopeId}`, [
            { name: "cl-pad", base: props.itemTitlePadding || "0px", tablet: props.itemTitlePaddingTablet, mobile: props.itemTitlePaddingMobile },
        ])}
        .${scopeId} .material-list {
            width: 100%;
            ${isGrid ? `display: flex; flex-wrap: wrap; align-items: stretch; gap: ${sc(props.cardGap || "20px")};` : ""}
        }

        .${scopeId} .material-card {
            width: ${isGrid ? sc(props.cardWidth || "300px") : "100%"};
            max-width: 100%;
            border-radius: ${sc(props.cardBorderRadius || "5px")};
            background-color: ${sc(cardBg)};
            border-color: ${sc(cardBorder)};
            border-style: solid;
            border-width: 1px;
            ${isHoriz ? `
            display: flex !important;
            flex-direction: ${variant === "horizontalReversed" ? "row-reverse" : "row"} !important;
            overflow: hidden;
            ` : ""}
        }
        ${isHoriz ? `
        .${scopeId} .material-card .material-card-image {
            width: 40%;
            min-width: 40%;
            height: 100% !important;
            min-height: 120px;
        }
        .${scopeId} .material-card .material-card-content {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            overflow: hidden;
            box-sizing: border-box;
        }
        .${scopeId} .material-card .material-card-content .material-card-main-btn,
        .${scopeId} .material-card .material-card-content .material-card-main-btn button,
        .${scopeId} .material-card .material-card-content .material-card-main-green-btn,
        .${scopeId} .material-card .material-card-content .material-card-btns .ant-btn-primary {
            max-width: 100% !important;
            box-sizing: border-box !important;
        }
        .${scopeId} .material-card .material-card-title-wrapper {
            max-width: 100%;
            overflow: hidden;
        }` : ""}
        ${isHoriz ? (variant === "horizontalReversed" ? `
        .${scopeId} .material-card-image img,
        .${scopeId} .material-card-image .material-card-image-placeholder {
            border-top-right-radius: ${sc(props.cardBorderRadius || "5px")};
            border-bottom-right-radius: ${sc(props.cardBorderRadius || "5px")};
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }` : `
        .${scopeId} .material-card-image img,
        .${scopeId} .material-card-image .material-card-image-placeholder {
            border-top-left-radius: ${sc(props.cardBorderRadius || "5px")};
            border-bottom-left-radius: ${sc(props.cardBorderRadius || "5px")};
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }`) : `
        .${scopeId} .material-card-image img,
        .${scopeId} .material-card-image .material-card-image-placeholder {
            border-top-left-radius: ${sc(props.cardBorderRadius || "5px")};
            border-top-right-radius: ${sc(props.cardBorderRadius || "5px")};
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }`}

        .${scopeId} .material-card-main-btn,
        .${scopeId} .material-card-main-btn button,
        .${scopeId} .material-card-btns .ant-btn-primary {
            background-color: ${sc(btnBg)} !important;
            border-color: ${sc(btnBg)} !important;
            color: ${sc(btnText)} !important;
            border-radius: ${sc(props.btnBorderRadius || "5px")} !important;
            font-size: ${sc(props.btnFontSize || "14px")} !important;
            padding: ${btnPad} !important;
            width: ${sc(props.btnWidth || "100%")} !important;
            height: auto !important;
        }

        .${scopeId} .material-card-main-green-btn {
            background-color: ${sc(btnStartBg)} !important;
            border-color: ${sc(btnStartBg)} !important;
            color: ${sc(btnStartText)} !important;
            border-radius: ${sc(props.btnBorderRadius || "5px")} !important;
            font-size: ${sc(props.btnFontSize || "14px")} !important;
            padding: ${btnPad} !important;
            width: ${sc(props.btnWidth || "100%")} !important;
            height: auto !important;
        }

        .${scopeId} .material-card-progress-current-name {
            color: ${sc(progressText)} !important;
            font-size: ${sc(props.progressTextSize || "13px")} !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            -webkit-line-clamp: ${nameLines} !important;
            line-clamp: ${nameLines} !important;
            overflow: hidden !important;
            min-height: calc(${nameLines} * 1.4em) !important;
        }
        .${scopeId} .ant-progress-inner {
            background-color: ${sc(progressBg)} !important;
            border-radius: ${sc(props.progressBorderRadius || "3px")} !important;
            height: ${sc(props.progressHeight || "6px")} !important;
        }
        .${scopeId} .ant-progress-bg {
            background-color: ${sc(progressBar)} !important;
            border-radius: ${sc(props.progressBorderRadius || "3px")} !important;
            height: ${sc(props.progressHeight || "6px")} !important;
        }

        .${scopeId} .material-card-title-wrapper {
            margin: ${sc(props.itemTitleMargin || "0")} !important;
            padding: var(--cl-pad, 12px 10px) !important;
        }

        ${!isBelowImage ? `
        .${scopeId} .material-card .material-card-image .material-card-title-wrapper {
            left: 10px !important;
            right: 10px !important;
            bottom: 10px !important;
            width: auto !important;
            background: transparent !important;
            text-shadow: none !important;
            padding: var(--cl-pad, 8px 12px) !important;
        }
        .${scopeId} .material-card .material-card-image .material-card-title-wrapper .material-card-title {
            text-shadow: none !important;
        }` : ""}
        ${props.itemTitleColor ? `
        .${scopeId} .material-card-title-wrapper,
        .${scopeId} .material-card-title {
            color: ${sc(props.itemTitleColor)} !important;
            text-shadow: none;
        }` : ""}

        ${isHoriz ? `
        .${scopeId} .material-card .material-card-image {
            width: ${sc(props.imageWidth || "40%")} !important;
            min-width: ${sc(props.imageWidth || "40%")} !important;
        }` : `
        .${scopeId} .material-card .material-card-image {
            height: ${sc(props.imageHeight || "160px")} !important;
        }`}

        ${isBelowImage ? `
        .${scopeId} .material-card .material-card-content .material-card-title-wrapper {
            position: static !important;
            background: none !important;
            color: ${sc(titleColor)} !important;
            text-shadow: none !important;
            margin: ${sc(props.itemTitleMargin || "0 0 10px 0")} !important;
            padding: var(--cl-pad, 0px) !important;
            width: auto !important;
        }
        .${scopeId} .material-card .material-card-content .material-card-title-wrapper .material-card-title {
            color: ${sc(titleColor)} !important;
            -webkit-line-clamp: unset !important;
        }` : ""}

        ${!props.showImage ? `
        .${scopeId} .material-card-image {
            display: none !important;
        }` : ""}

        ${!props.showProgress ? `
        .${scopeId} .material-card-progress,
        .${scopeId} .material-card-progress-current-name {
            display: none !important;
        }` : ""}

        ${!props.showDescription ? `
        .${scopeId} .material-card-desc,
        .${scopeId} .material-card-content > .flex.flex-col {
            display: none !important;
        }` : ""}

        ${!props.showDescriptionLabel ? `
        .${scopeId} [data-testid="lib-desc"] > *:first-child,
        .${scopeId} [data-testid="course-desc"] > *:first-child,
        .${scopeId} [data-testid="folder-desc"] > *:first-child {
            display: none !important;
        }` : ""}

        ${generateBtnHoverCss(`.${scopeId}`, ".material-card-main-btn, .material-card-main-btn button, .material-card-btns .ant-btn-primary, .material-card-main-green-btn", props)}

        .${scopeId} .material-card-folder-badge {
            ${props.folderBorderRadius ? `border-radius: ${sc(props.folderBorderRadius)} !important;` : ""}
            ${props.folderBorderColor ? `border: 1px solid ${sc(props.folderBorderColor)} !important;` : ""}
            background-color: ${props.folderBgColor ? sc(props.folderBgColor) : "rgba(0,0,0,0.55)"} !important;
            color: ${props.folderTextColor ? sc(props.folderTextColor) : "#fff"} !important;
            ${props.folderFontSize ? `font-size: ${sc(props.folderFontSize)} !important;` : ""}
            ${(props.folderMarginTop || props.folderMarginRight || props.folderMarginBottom || props.folderMarginLeft)
                ? `margin: ${sc(props.folderMarginTop || "0px")} ${sc(props.folderMarginRight || "0px")} ${sc(props.folderMarginBottom || "0px")} ${sc(props.folderMarginLeft || "0px")} !important;`
                : ""}
            ${(props.folderPaddingTop || props.folderPaddingRight || props.folderPaddingBottom || props.folderPaddingLeft)
                ? `padding: ${sc(props.folderPaddingTop || "2px")} ${sc(props.folderPaddingRight || "10px")} ${sc(props.folderPaddingBottom || "2px")} ${sc(props.folderPaddingLeft || "10px")} !important;`
                : ""}
        }
        .${scopeId} .ant-select .ant-select-selector {
            ${props.filterBgColor ? `background-color: ${sc(props.filterBgColor)} !important;` : ""}
        }
        .${scopeId} .ant-select .ant-select-selection-item,
        .${scopeId} .ant-select .ant-select-selection-placeholder {
            ${props.filterTextColor ? `color: ${sc(props.filterTextColor)} !important;` : ""}
            ${props.filterFontSize ? `font-size: ${sc(props.filterFontSize)} !important;` : ""}
        }
        .${scopeId} .ant-select-focused .ant-select-selector,
        .${scopeId} .ant-select .ant-select-selector:hover {
            ${props.filterActiveColor ? `border-color: ${sc(props.filterActiveColor)} !important;` : ""}
        }
    `;

    const renderFolderMode = () => {
        const items = folderItems;
        const hasItems = items.length > 0;
        return (
            <>
                {searchFilterBar}
                <SchoolContentBreadcrumb folder={folder} onSelect={openFolder} />
                <div className="material-list" style={containerStyle}>
                    {hasItems ? items.map((x) =>
                        x.type === SchoolContentItemType.Item ? (
                            x.course && needCourses ? (
                                <CourseCard key={x.id} course={x.course} titlePosition={titlePosition} buttonLabels={courseButtonLabels} />
                            ) : x.library && needLibraries ? (
                                <LibraryCard key={x.id} library={x.library} titlePosition={titlePosition} buttonLabel={props.btnOpenText || undefined} />
                            ) : null
                        ) : (
                            <FolderCard
                                key={x.id}
                                content={x}
                                sectionType={findFolderSection(x, sourceFilterOpts) ?? sectionType}
                                onOpen={() => openFolder(x)}
                                titlePosition={titlePosition}
                            />
                        ),
                    ) : (
                        <div style={{ color: "#aaa", padding: "20px" }}>
                            {loc.word("CourseList.noItems", { default: "No items to display" })}
                        </div>
                    )}
                </div>
            </>
        );
    };

    const searchBarAlign = props.searchBarAlign || "left";
    const searchBarJustify = searchBarAlign === "center" ? "center" : searchBarAlign === "right" ? "flex-end" : "flex-start";
    const searchBarWidth = sc(props.searchBarWidth || "100%");

    const filterOptions = [
        { value: "all", label: loc.word("CourseList.filterAll", { default: "All courses" }) },
        { value: "started", label: loc.word("CourseList.filterStarted", { default: "Started courses" }) },
        { value: "available", label: loc.word("CourseList.filterAvailable", { default: "Available courses" }) },
    ];

    const searchFilterBar = (props.showSearch || props.showFilter) && (
        <div style={{ display: "flex", justifyContent: searchBarJustify, marginBottom: "16px" }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? "8px" : "12px",
                flexWrap: "nowrap",
                width: searchBarWidth,
                maxWidth: "100%",
            }}>
                {props.showSearch && (
                    <Input
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={props.searchPlaceholder || loc.word("CourseList.searchPlaceholder", { default: "Search" })}
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        allowClear
                        style={{
                            flex: isMobile ? "1 1 50%" : "1 1 auto",
                            minWidth: 0,
                            fontSize: sc(props.searchFontSize || "14px"),
                            color: props.searchTextColor || token.colorText,
                            backgroundColor: props.searchBgColor || token.colorBgContainer,
                            border: `1px solid ${props.searchBorderColor || token.colorBorder}`,
                            borderRadius: sc(props.searchBorderRadius || "6px"),
                        }}
                    />
                )}
                {props.showFilter && needCourses && (
                    <Select
                        value={courseFilter}
                        onChange={(v) => setCourseFilter(v as "all" | "started" | "available")}
                        options={filterOptions}
                        style={{
                            flex: isMobile ? "1 1 50%" : "0 0 auto",
                            minWidth: isMobile ? 0 : 180,
                            fontSize: props.filterFontSize || "14px",
                        }}
                    />
                )}
            </div>
        </div>
    );

    const renderFlatMode = () => (
        <>
            {searchFilterBar}
            <div className="material-list" style={containerStyle}>
                {needCourses && filteredCourses.map((course) => (
                    <CourseCard key={course.id} course={course} titlePosition={titlePosition} buttonLabels={courseButtonLabels} />
                ))}
                {needLibraries && filteredLibraries.map((library) => (
                    <LibraryCard key={library.id} library={library} titlePosition={titlePosition} buttonLabel={props.btnOpenText || undefined} />
                ))}
                {filteredCourses.length === 0 && filteredLibraries.length === 0 && (
                    <div style={{ color: "#aaa", padding: "20px" }}>
                        {searchText
                            ? loc.word("CourseList.noSearchResults", { default: "Nothing found" })
                            : loc.word("CourseList.noItems", { default: "No items to display" })}
                    </div>
                )}
            </div>
        </>
    );

    return (
        <div
            className={scopeId}
            style={{
                width: "100%",
                minWidth: 0,
            }}
        >
            <style>{scopedCss}</style>
            {isFolderMode ? renderFolderMode() : renderFlatMode()}
        </div>
    );
};

export const parseCourseListProps = (container: HTMLElement): Omit<CourseListWidgetProps, "sdk" | "folderParam"> => {
    const a = (name: string, fallback = "") => attr(container, name, fallback);
    const b = (name: string, fallback = false) => bool(container, name, fallback);
    const parseJsonArray = (name: string): string[] | undefined => {
        const val = container.getAttribute(name);
        if (!val) return undefined;
        try { return JSON.parse(val); } catch { return undefined; }
    };

    return {
        sourceMode: (a("data-source-mode", "allCourses") as SourceMode),
        displayMode: (a("data-display-mode", "flat") as DisplayMode),
        layout: (a("data-layout", "grid") as "grid" | "list"),
        showProgress: b("data-show-progress", true),
        showDescription: b("data-show-description", true),
        showDescriptionLabel: b("data-show-description-label", true),
        showImage: b("data-show-image", true),
        cardVariant: (a("data-card-variant", "vertical") as any),
        cardGap: a("data-card-gap", "20px"),
        cardWidth: a("data-card-width", "300px"),
        cardBorderRadius: a("data-card-border-radius", "5px"),
        cardBgColor: a("data-card-bg-color", "#ffffff"),
        cardBorderColor: a("data-card-border-color", "#f0f0f0"),
        itemTitlePosition: (a("data-item-title-position", "onImage") as "onImage" | "belowImage"),
        itemTitleColor: a("data-item-title-color"),
        itemTitleMargin: a("data-item-title-margin", "0px"),
        itemTitlePadding: a("data-item-title-padding", "0px"),
        itemTitlePaddingTablet: a("data-item-title-padding-tablet", ""),
        itemTitlePaddingMobile: a("data-item-title-padding-mobile", ""),
        imageWidth: a("data-image-width", "40%"),
        imageHeight: a("data-image-height", "160px"),
        btnBgColor: a("data-btn-bg-color", "#1890ff"),
        btnTextColor: a("data-btn-text-color", "#ffffff"),
        btnBorderRadius: a("data-btn-border-radius", "5px"),
        btnFontSize: a("data-btn-font-size", "14px"),
        btnPaddingV: a("data-btn-padding-v", "4px"),
        btnPaddingH: a("data-btn-padding-h", "15px"),
        btnWidth: a("data-btn-width", "100%"),
        progressBarColor: a("data-progress-bar-color", "#1890ff"),
        progressBgColor: a("data-progress-bg-color", "#f0f0f0"),
        progressHeight: a("data-progress-height", "6px"),
        progressBorderRadius: a("data-progress-border-radius", "3px"),
        progressTextColor: a("data-progress-text-color", "#999"),
        progressTextSize: a("data-progress-text-size", "13px"),
        progressNameLines: a("data-progress-name-lines", "2"),
        btnContinueText: a("data-btn-continue-text"),
        btnStartText: a("data-btn-start-text"),
        btnOpenText: a("data-btn-open-text"),
        btnStartBgColor: a("data-btn-start-bg-color", "#52c41a"),
        btnStartTextColor: a("data-btn-start-text-color", "#ffffff"),
        showSearch: b("data-show-search"),
        showFilter: b("data-show-filter"),
        searchPlaceholder: a("data-search-placeholder"),
        searchBgColor: a("data-search-bg-color", "#ffffff"),
        searchTextColor: a("data-search-text-color", "#333333"),
        searchBorderColor: a("data-search-border-color", "#d9d9d9"),
        searchBorderRadius: a("data-search-border-radius", "6px"),
        searchFontSize: a("data-search-font-size", "14px"),
        filterBgColor: a("data-filter-bg-color"),
        filterActiveColor: a("data-filter-active-color", "#1890ff"),
        filterTextColor: a("data-filter-text-color", "#666666"),
        filterFontSize: a("data-filter-font-size", "14px"),
        searchBarAlign: (a("data-search-bar-align", "left") as any),
        searchBarWidth: a("data-search-bar-width", "100%"),
        folderBorderRadius: a("data-folder-border-radius", "5px"),
        folderBorderColor: a("data-folder-border-color", ""),
        folderBgColor: a("data-folder-bg-color", ""),
        folderTextColor: a("data-folder-text-color", ""),
        folderFontSize: a("data-folder-font-size", "14px"),
        folderMarginTop: a("data-folder-margin-top", "0px"),
        folderMarginRight: a("data-folder-margin-right", "0px"),
        folderMarginBottom: a("data-folder-margin-bottom", "0px"),
        folderMarginLeft: a("data-folder-margin-left", "0px"),
        folderPaddingTop: a("data-folder-padding-top", "12px"),
        folderPaddingRight: a("data-folder-padding-right", "12px"),
        folderPaddingBottom: a("data-folder-padding-bottom", "12px"),
        folderPaddingLeft: a("data-folder-padding-left", "12px"),
        courseIds: parseJsonArray("data-course-ids"),
        libraryIds: parseJsonArray("data-library-ids"),
        ...parseBtnHoverProps(container),
    };
};
